import type {
  Availability,
  EvidenceRelation,
  Explanation,
  FindingStatus,
  Outcome,
  Role,
  SourceKind,
} from '../api/types';

const dateFormatter = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
const monthFormatter = new Intl.DateTimeFormat('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });
const dateTimeFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'UTC',
  timeZoneName: 'short',
});

function parse(iso: string | null | undefined): Date | null {
  if (iso === null || iso === undefined || iso === '') return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Unknown dates are said plainly; they are never replaced by a guess. */
export function formatDate(iso: string | null | undefined, unknown = 'Date not established'): string {
  const date = parse(iso);
  return date === null ? unknown : dateFormatter.format(date);
}

export function formatMonth(iso: string | null | undefined): string {
  const date = parse(iso);
  return date === null ? 'Date not established' : monthFormatter.format(date);
}

export function formatDateTime(iso: string | null | undefined): string {
  const date = parse(iso);
  return date === null ? 'Time not recorded' : dateTimeFormatter.format(date);
}

export function formatRange(start: string | null | undefined, end: string | null | undefined): string {
  const from = parse(start);
  const to = parse(end);
  if (from === null && to === null) return 'Date not established';
  if (from !== null && to === null) return `From ${dateFormatter.format(from)}`;
  if (from === null && to !== null) return `Until ${dateFormatter.format(to)}`;
  if (from !== null && to !== null) {
    const a = dateFormatter.format(from);
    const b = dateFormatter.format(to);
    return a === b ? a : `${a} – ${b}`;
  }
  return 'Date not established';
}

export function yearOf(iso: string | null | undefined): number | null {
  const date = parse(iso);
  return date === null ? null : date.getUTCFullYear();
}

/** Explanatory fields can be a paragraph or a list; normalise without dropping empties silently. */
export function asList(value: Explanation | null | undefined): string[] {
  if (value === null || value === undefined) return [];
  const items = Array.isArray(value) ? value : [value];
  return items.map((item) => item.trim()).filter((item) => item !== '');
}

export interface StatusMeta {
  label: string;
  description: string;
  symbol: string;
}

/** Four statuses, each with its own word, symbol and colour so none relies on colour alone. */
export const statusMeta: Record<FindingStatus, StatusMeta> = {
  proposed: {
    label: 'Proposed',
    symbol: '○',
    description: 'Drafted from the evidence and open for member discussion. Not yet reviewed by a steward.',
  },
  reviewed: {
    label: 'Reviewed',
    symbol: '●',
    description: 'A steward reviewed this version against its evidence and published it with a recorded reason.',
  },
  contested: {
    label: 'Contested',
    symbol: '◐',
    description: 'A steward recorded an unresolved disagreement. Read the challenge and decision before relying on it.',
  },
  superseded: {
    label: 'Superseded',
    symbol: '↺',
    description: 'Replaced by a later version or finding. Kept for history; do not cite it as current.',
  },
};

export const relationMeta: Record<EvidenceRelation, { label: string; plural: string; description: string }> = {
  supports: { label: 'Supports', plural: 'Supporting evidence', description: 'Material consistent with the finding.' },
  contradicts: {
    label: 'Conflicts',
    plural: 'Conflicting evidence',
    description: 'Material that cuts against the finding or complicates it.',
  },
  context: { label: 'Context', plural: 'Context', description: 'Background needed to read the evidence fairly.' },
};

export const sourceKindLabel: Record<SourceKind, string> = {
  discord: 'Discord',
  notion: 'Notion',
  repository: 'Repository',
  map: 'Original map',
  manual: 'Manual record',
};

export const availabilityMeta: Record<Availability, { label: string; tone: 'ok' | 'warn' | 'gap' }> = {
  available: { label: 'Available', tone: 'ok' },
  metadata_only: { label: 'Metadata only', tone: 'warn' },
  missing: { label: 'Missing', tone: 'gap' },
  deleted: { label: 'Deleted at source', tone: 'gap' },
  forbidden: { label: 'Access not granted', tone: 'gap' },
  error: { label: 'Acquisition error', tone: 'gap' },
};

export const roleLabel: Record<Role, string> = { member: 'Member', steward: 'Steward', owner: 'Owner' };

export const outcomeLabel: Record<Outcome, string> = {
  positive: 'Helped / happened',
  negative: 'Did not help / did not happen',
  unknown: 'Not known yet',
};

export function isSafeExternalUrl(url: string | undefined): url is string {
  if (url === undefined) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}

export function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** Splits authored prose on blank lines. */
export function paragraphs(text: string | null | undefined): string[] {
  if (text === null || text === undefined) return [];
  return text
    .split(/\n\s*\n/)
    .map((part) => part.trim())
    .filter((part) => part !== '');
}

export function chapterNumber(index: number): string {
  return String(index + 1).padStart(2, '0');
}

/** Research evidence classes, described by what they can and cannot show. */
export const evidenceClassMeta: Record<string, { label: string; caution?: string }> = {
  participant_report: { label: 'Participant report', caution: 'A self-report, not an independent check.' },
  reported_action_outcome: { label: 'Reported outcome', caution: 'Reported, not verified against an artifact.' },
  artifact_check: { label: 'Artifact check' },
  help_request: { label: 'Help request' },
  help_response: { label: 'Help response', caution: 'A response is not proof the help was useful.' },
  help_acceptance: { label: 'Accepted help' },
  workflow_snapshot: { label: 'Workflow snapshot', caution: 'A task status snapshot is not a delivery date.' },
  secondary_synthesis: { label: 'Secondary synthesis', caution: 'A clean research account, not independent corroboration.' },
  copy_or_template: { label: 'Copy or template', caution: 'Repeated text is not an independent voice.' },
  bot_output: { label: 'Bot output', caution: 'Automated, not human activity.' },
  unresolved_or_censored: { label: 'Unresolved or censored', caution: 'The outcome is not known within the captured scope.' },
  vision_artifact: { label: 'Vision artifact', caution: 'An intention or plan, not a record of practice.' },
  proposal: { label: 'Proposal', caution: 'A proposal is not an agreement.' },
};

export function evidenceClassLabel(value: string | undefined): string | null {
  if (value === undefined || value === '') return null;
  return evidenceClassMeta[value]?.label ?? value.replace(/_/g, ' ');
}

export function themeLabel(theme: string): string {
  return theme.replace(/-/g, ' ');
}

export const decisionMeta = {
  publish: { label: 'Publish as reviewed', verb: 'Published', result: 'reviewed' },
  contest: { label: 'Record as contested', verb: 'Contested', result: 'contested' },
  supersede: { label: 'Supersede', verb: 'Superseded', result: 'superseded' },
} as const;

export const annotationKindMeta = {
  context: {
    label: 'Add context',
    noun: 'Context',
    help: 'Background that helps others read the evidence fairly. Does not ask for a change.',
  },
  correction: {
    label: 'Correct a fact',
    noun: 'Correction',
    help: 'Something stated is wrong or out of date. Flags the finding for steward review.',
  },
  challenge: {
    label: 'Challenge the interpretation',
    noun: 'Challenge',
    help: 'The evidence is read correctly but the conclusion does not follow. Flags the finding for review.',
  },
  proposal: {
    label: 'Propose a revision',
    noun: 'Proposal',
    help: 'Suggest new wording, scope or evidence. A steward decides, with a recorded reason.',
  },
} as const;
